import React, { useCallback, useEffect, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { z } from 'zod';

import Input from 'components/Input/Input';
import InputSelectDropdown, {
  InputSelectDropdownOption
} from 'components/InputSelectDropdown/InputSelectDropdown';
import { ETemplateStudioLayoutOptions } from 'components/TemplatesStudio/enums';
import { Checkbox } from 'components/ui/checkbox';
import { FormControl, FormField, FormItem, FormMessage } from 'components/ui';
import { Separator } from 'components/ui/separator';

import { templateStudioSchema } from '../../../validation-schema';
import { FreeTilesLayoutPicker } from '../Components/FreeTilesLayoutPicker';
import { InlineFormRow } from '../Components/InlineFormRow';
import { ThemesFormItem } from '../Components/ThemesFormItem';

import { seedBuyOneGetManyConfig } from './BuyOneGetMany.seed';

export const TemplateStudioBuyOneGetManyForm: React.FC = () => {
  const { control, watch, setValue } =
    useFormContext<z.infer<typeof templateStudioSchema>>();
  const { supportedThemesForRollingOffers } = useTemplateStudioState();

  const desktopLayoutOptions = useMemo<InputSelectDropdownOption[]>(
    () =>
      Object.values(ETemplateStudioLayoutOptions).map((value) => ({
        value,
        label: value
      })),
    []
  );

  const currentValue = watch('buyOneGetMany');

  const handleEnsureBlockConfig = useCallback((): void => {
    const next = seedBuyOneGetManyConfig(currentValue);
    if (!next) return;
    setValue('buyOneGetMany', next, {
      shouldDirty: true,
      shouldValidate: true
    });
  }, [currentValue, setValue]);

  useEffect(() => {
    handleEnsureBlockConfig();
  }, [handleEnsureBlockConfig]);

  if (!supportedThemesForRollingOffers || !currentValue) return null;

  return (
    <React.Fragment>
      <Separator className="bg-base-border w-[288px] ml-[-2px]" />

      <FormField
        control={control}
        name="buyOneGetMany.themes"
        render={({ field }) => (
          <ThemesFormItem
            label="Design Theme"
            onChange={field.onChange}
            value={field.value || []}
            supportedThemes={supportedThemesForRollingOffers}
            themesSelectDataTestId="TemplatesStudio-BuyOneGetMany-themes"
          />
        )}
      />

      <FormField
        control={control}
        name="buyOneGetMany.desktopLayout"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <InlineFormRow label="Desktop Layout">
                <InputSelectDropdown
                  options={desktopLayoutOptions}
                  defaultSelected={desktopLayoutOptions.find(
                    (layout) =>
                      layout.value === watch('buyOneGetMany.desktopLayout')
                  )}
                  onOptionChange={field.onChange}
                  placeholder="Select desktop layout"
                  isHorizontal={true}
                  buttonClassName={'h-[30px]'}
                  iconClassName="text-xs text-base-mutedForeground"
                  data-testid="TemplatesStudio-BuyOneGetMany-desktopLayout-label"
                  buttonDataTestId="TemplatesStudio-BuyOneGetMany-desktopLayout-button"
                  listDataTestId="TemplatesStudio-BuyOneGetMany-desktopLayout-list"
                />
              </InlineFormRow>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="buyOneGetMany.maxOffers"
        render={({ field }) => {
          const isUnlimited = !!watch('buyOneGetMany.unlimited');
          return (
            <FormItem>
              <FormControl>
                <InlineFormRow label="Max Offers">
                  <Input
                    name="buyOneGetMany.maxOffers"
                    type="number"
                    min={1}
                    disabled={isUnlimited}
                    value={field.value}
                    inputClassName="w-full h-[30px]"
                    onChange={(event) =>
                      field.onChange(Number(event.target.value))
                    }
                    dataTestId="TemplatesStudio-BuyOneGetMany-maxOffers"
                  />
                </InlineFormRow>
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      <FormField
        control={control}
        name="buyOneGetMany.unlimited"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-2 mt-2">
              <FormControl>
                <Checkbox
                  id="TemplatesStudio-BuyOneGetMany-unlimited"
                  checked={field.value}
                  onCheckedChange={(checked) =>
                    field.onChange(checked === true)
                  }
                  data-testid="TemplatesStudio-BuyOneGetMany-unlimited"
                />
              </FormControl>
              <label
                htmlFor="TemplatesStudio-BuyOneGetMany-unlimited"
                className="text-sm text-base-mutedForeground whitespace-nowrap cursor-pointer"
              >
                Unlimited
              </label>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="buyOneGetMany.freeTilesLayout"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <FreeTilesLayoutPicker
                value={field.value}
                onChange={field.onChange}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </React.Fragment>
  );
};
